import moment from "moment";

declare module "moment" {
  interface Moment {
    round(precision: number, key: string, direction?: "round" | "ceil" | "floor"): Moment;
    ceil(precision: number, key: string): Moment;
    floor(precision: number, key: string): Moment;
  }
}

type RoundDirection = "round" | "ceil" | "floor";

interface MethodConfig {
  name: "Hours" | "Minutes" | "Seconds" | "Milliseconds";
  maxValue: number;
}

const methods: Record<string, MethodConfig> = {
  hours: { name: "Hours", maxValue: 24 },
  minutes: { name: "Minutes", maxValue: 60 },
  seconds: { name: "Seconds", maxValue: 60 },
  milliseconds: { name: "Milliseconds", maxValue: 1000 }
};

const keys: Record<string, string> = {
  mm: "milliseconds",
  milliseconds: "milliseconds",
  Milliseconds: "milliseconds",
  s: "seconds",
  seconds: "seconds",
  Seconds: "seconds",
  m: "minutes",
  minutes: "minutes",
  Minutes: "minutes",
  H: "hours",
  h: "hours",
  hours: "hours",
  Hours: "hours"
};

(moment.fn as unknown as Record<string, unknown>).round = function (precision: number, key: string, direction: RoundDirection = "round"): moment.Moment {
  let normalizedKey = key;
  if (normalizedKey.length > 1 && normalizedKey !== "mm" && !normalizedKey.endsWith("s")) {
    normalizedKey += "s";
  }

  const mappedKey = keys[normalizedKey]?.toLowerCase();
  if (!mappedKey || !methods[mappedKey]) {
    throw new Error("The value to round is not valid. Possibles [\"hours\", \"minutes\", \"seconds\", \"milliseconds\"]");
  }

  const targetMethod = methods[mappedKey];
  let value = 0;
  let rounded = false;
  let subRatio = 1;
  let maxValue = 0;

  const self = this as unknown as { _d: Date };
  const dateObj = self._d;

  for (const k in methods) {
    if (k === mappedKey) {
      value = (dateObj as unknown as Record<string, () => number>)[`get${targetMethod.name}`]();
      maxValue = targetMethod.maxValue;
      rounded = true;
    } else if (rounded) {
      subRatio *= methods[k].maxValue;
      const subVal = (dateObj as unknown as Record<string, () => number>)[`get${methods[k].name}`]();
      value += subVal / subRatio;
      (dateObj as unknown as Record<string, (v: number) => void>)[`set${methods[k].name}`](0);
    }
  }

  value = Math[direction](value / precision) * precision;
  value = Math.min(value, maxValue);
  (dateObj as unknown as Record<string, (v: number) => void>)[`set${targetMethod.name}`](value);

  return this as unknown as moment.Moment;
};

(moment.fn as unknown as Record<string, unknown>).ceil = function (precision: number, key: string): moment.Moment {
  return (this as unknown as { round: (p: number, k: string, d: string) => moment.Moment }).round(precision, key, "ceil");
};

(moment.fn as unknown as Record<string, unknown>).floor = function (precision: number, key: string): moment.Moment {
  return (this as unknown as { round: (p: number, k: string, d: string) => moment.Moment }).round(precision, key, "floor");
};

export default moment;
